import {
  ChipBadge,
  IconWrapper,
  ImageWrapper,
  theme,
  VideoPlayerWrapper,
} from '@geneo2-web/shared-ui';

import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Dialog,
  IconButton,
  Modal,
  Slide,
  SxProps,
  Typography,
  useMediaQuery,
} from '@mui/material';
import React, { useState } from 'react';
import { NoContentCard } from '../NoContent.tsx';
import { pxToRem, pxTovW } from '../../../commonUtils/resizeUtils';
import { IStyles } from '../../../commonUtils/styleUtils';

const styles: IStyles = {
  heading: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(2), md: pxTovW(8) },
    paddingBottom: 0,
  },
  itemWrapper: {
    background: {
      xs: 'linear-gradient(to bottom, white 33%,#FCF1C7 33% 66%,white 66% 100%)',
      md: 'linear-gradient(to bottom, white 40%,#EAF4FC 20% 80%)',
    },
    display: 'flex',
    overflowX: 'auto',
    justifyContent: 'flex-start',
    gap: { xs: pxToRem(16), md: pxTovW(18) },
    paddingTop: { xs: pxToRem(20), md: pxTovW(20) },
    paddingBottom: { xs: 0, md: pxTovW(40) },
    '&::-webkit-scrollbar': { display: 'none' },
    paddingLeft: { xs: pxToRem(22), md: pxTovW(22) },
    paddingRight: { xs: pxToRem(22), md: pxTovW(22) },
    boxSizing: 'border-box',
    '&>div': { flexShrink: 0 },
  },

  cardRoot: {
    position: 'relative', // ADD THIS LINE
    width: { xs: pxToRem(180), md: pxTovW(319) },
    height: { xs: pxToRem(220), md: pxTovW(350), lg: pxTovW(240) }, // ✅ FIX: add xs height
    border: '1px solid #CCE6FE',
    borderRadius: pxTovW(10),
    overflow: 'hidden',
    backgroundColor: '#fff',
    display: 'flex',
    flexDirection: 'column',
    cursor: 'pointer',
    '&:hover': {
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
    },
  },

  cardImage: {
    position: 'relative',
    // backgroundColor: '#354B8A',
    height: { xs: pxToRem(120), md: pxTovW(140) },
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: '#fff',
    fontSize: pxToRem(16),
    fontWeight: 600,
    textAlign: 'center',
    padding: pxToRem(8),
    // borderBottom: '1px solid #CCE6FE', // ADD THIS LINE
    // borderRadius: ,
  },
  playIcon: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    fontSize: { xs: pxToRem(40), md: pxTovW(50) },
    color: '#FF2E2E',
  },
  cardFooter: {
    padding: { lg: pxTovW(2) },
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: pxToRem(5),
  },
};

interface IVideoData {
  title: string;
  duration: string;
  thumbnailUrl?: string; // optional
  videoUrl: string; // ADD THIS
}

interface IProps {
  title: string;
  videos: IVideoData[];
  linkButtonHandler?: () => void;
  itemsToShow?: number;
  containerMdWidth: string;
  totalNoOfItems?: number;
  isError?: boolean;
  errorMessage?: string;
  noContentMessage?: string;
}
export const HomeVideoCard = (props: IProps) => {
  const {
    title,
    videos,
    linkButtonHandler,
    itemsToShow = 4,
    containerMdWidth,
    totalNoOfItems,
    isError,
    errorMessage,
    noContentMessage,
  } = props;

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [currentIndex, setCurrentIndex] = useState(0);
  const [videoUrl, setVideoUrl] = useState<string>('');
  const containerRef = React.useRef(null);
  const totalPages = Math.ceil(videos.length / itemsToShow);

  const handleDotClick = (pageIndex: number) => {
    setCurrentIndex(pageIndex * itemsToShow);
  };

  return (
    <Box sx={{ width: { xs: '100%', md: containerMdWidth } }}>
      {/* Header */}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxSizing: 'border-box',
          padding: {
            xs: `${pxToRem(20)} ${pxToRem(8)}`,
            md: `${pxTovW(18)} ${pxTovW(24)}`,
          },
        }}
      >
        <Box sx={styles.heading}>
          <Typography
            variant="h2"
            fontWeight="medium"
            sx={{
              width: '100%',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              wordBreak: 'break-word',
              whiteSpace: 'normal',
              margin: 'auto',
            }}
          >
            {title}
          </Typography>
          <ChipBadge
            label={totalNoOfItems ? totalNoOfItems : videos?.length}
            color="primary"
            size="small"
          />
        </Box>
        {/* <Typography
          variant="h4"
          onClick={linkButtonHandler}
          sx={{
            color: '#007CDC',
            textDecorationColor: '#007CDC',
            textDecorationLine: 'underline',
            cursor: 'pointer',
          }}
        >
          See All
        </Typography> */}
      </Box>

      {/* Content */}
      {isError ? (
        <Box
          width={{ xs: undefined, md: '100%' }}
          marginY={{ xs: pxToRem(20), md: pxTovW(40) }}
          marginLeft={{ xs: 0, md: pxTovW(40) }}
        >
          <NoContentCard
            variant="error"
            icon="error"
            text={errorMessage || 'Error Occurred'}
          />
        </Box>
      ) : videos.length === 0 ? (
        <NoContentCard
          variant="info"
          icon="cards"
          text={noContentMessage || 'No Data to show'}
          rootStyle={{ marginTop: { xs: pxToRem(20), md: pxTovW(40) } }}
        />
      ) : (
        <>
          {/* Cards */}
          <Box sx={styles.itemWrapper} ref={containerRef}>
            {videos.map((video, index) => {
              const isVisible =
                index >= currentIndex && index < currentIndex + itemsToShow;
              return (
                <Slide
                  key={index}
                  direction={isVisible ? 'left' : 'right'}
                  in={isMobile ? true : isVisible}
                  mountOnEnter
                  unmountOnExit
                  container={containerRef.current}
                >
                  <Box
                    sx={styles.cardRoot}
                    onClick={() => setVideoUrl(video.videoUrl)}
                  >
                    <Box sx={styles.cardImage}>
                      <ImageWrapper
                        name={video.thumbnailUrl ?? ''}
                        type="png"
                        parentFolder="images"
                        styles={{
                          width: { xs: pxToRem(150), lg: pxTovW(320) },
                          height: { xs: pxToRem(100), lg: pxTovW(130) },
                          borderRadius: pxToRem(10),
                        }}
                      />
                      <IconWrapper
                        name="video-red"
                        type="png"
                        parentFolder="icons"
                        customSx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          width: pxToRem(42),
                          height: pxToRem(42),
                        }}
                      />
                    </Box>

                    {/* Title content that will take available space */}
                    <Box
                      sx={{
                        padding: { xs: pxToRem(10), lg: pxTovW(5) },
                        flexGrow: 1, // THIS LINE ensures it takes available space
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        textAlign: 'center',
                      }}
                    >
                      <Typography
                        variant="h4"
                        fontWeight="bold"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2, // Limit max 2 lines
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          paddingTop: {
                            md: pxTovW(5),
                          },
                        }}
                      >
                        {video.title}
                      </Typography>
                    </Box>

                    {/* Footer (duration) will always stick to bottom now */}
                    <Box sx={styles.cardFooter}>
                      <IconWrapper
                        name="clock"
                        size="small"
                        parentFolder="icons"
                        type="png"
                      />
                      <Typography variant="h4">{video.duration}</Typography>
                    </Box>
                  </Box>
                </Slide>
              );
            })}
          </Box>

          {/* Dot Carousel */}
          {!isMobile && videos.length > itemsToShow && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mt: { xs: pxToRem(12), md: pxTovW(18) },
                gap: pxToRem(8),
              }}
            >
              {Array.from({ length: totalPages }).map((_, pageIndex) => (
                <Box
                  key={pageIndex}
                  onClick={() => handleDotClick(pageIndex)}
                  sx={{
                    width: pxToRem(10),
                    height: pxToRem(10),
                    borderRadius: '50%',
                    backgroundColor:
                      currentIndex / itemsToShow === pageIndex
                        ? '#F8C807'
                        : '#FEEDAE',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                  }}
                />
              ))}
            </Box>
          )}
        </>
      )}

      <Dialog
        open={!!videoUrl}
        onClose={() => setVideoUrl('')}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            backgroundColor: 'transparent', // No background, only player
            boxShadow: 'none',
          },
        }}
      >
        {/* Close button */}
        <IconButton
          onClick={() => setVideoUrl('')}
          sx={{
            position: 'absolute',
            top: pxToRem(10),
            right: pxToRem(10),
            zIndex: 1,
            color: '#fff',
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Video player */}
        <VideoPlayerWrapper videoUrl={videoUrl} />
      </Dialog>
    </Box>
  );
};
